import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, UserCog, X } from "lucide-react";
import { LOGO_URL } from "@/lib/assets";

export const scrollToSection = (hash) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(hash, { offset: -72, duration: 1.4 });
  } else {
    document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  }
};

const MENU_LINKS = [
  { label: "Home", href: "#top", testid: "menu-home" },
  { label: "Volunteer", href: "#volunteer", testid: "menu-volunteer" },
  { label: "Legacy", href: "#legacy", testid: "menu-legacy" },
  { label: "Classes", href: "#classes", testid: "menu-classes" },
  { label: "Schedule", href: "#schedule", testid: "menu-schedule" },
  { label: "News", href: "#news", testid: "menu-news" },
  { label: "Gallery", href: "/gallery", testid: "menu-gallery" },
  { label: "Committee", href: "/committee", testid: "menu-committee" },
  { label: "Sponsors", href: "/sponsors", testid: "menu-sponsors" },
  { label: "Live Results", href: "/live-results", testid: "menu-live-results", accent: true },
  { label: "Contact", href: "#contact", testid: "menu-contact" },
  { label: "Visit & Contact", href: "#visit", testid: "menu-visit" },
  { label: "Nominate Now", href: "nominate", testid: "menu-nominate" },
  { label: "Club Admin", href: "/admin", testid: "menu-admin" },
];

export default function Navbar({ onJoin, onNominate }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const go = (href) => {
    setMenuOpen(false);
    if (href === "nominate") {
      onNominate?.();
      return;
    }
    if (href.startsWith("#")) {
      scrollToSection(href);
    } else {
      window.location.href = href;
    }
  };

  return (
    <>
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/70 border-b border-clay-border/60"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-4 sm:px-8 py-4">
        <a
          data-testid="nav-logo"
          href="#top"
          onClick={(e) => { e.preventDefault(); scrollToSection("#top"); }}
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO_URL}
            alt="Mid Western Speedway logo"
            className="w-10 h-10 object-cover rounded-sm ring-1 ring-clay-border group-hover:ring-clay-orange transition-all duration-300"
          />
          <span className="font-display font-800 text-lg tracking-wide uppercase leading-none">
            Mid Western <span className="text-clay-orange">Speedway</span>
            <span className="block font-mono text-[10px] tracking-[0.3em] text-clay-muted font-normal">
              Darlington VIC — Est. 1968
            </span>
          </span>
        </a>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-nominate-button"
            onClick={onNominate}
            className="hidden sm:block border border-clay-orange text-clay-orange hover:bg-clay-orange hover:text-clay-bg font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 transition-all duration-300"
          >
            Nominate
          </button>
          <a
            data-testid="nav-admin-button"
            href="/admin"
            className="hidden sm:flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-clay-muted hover:text-clay-text transition-colors duration-300"
          >
            <UserCog className="w-4 h-4" /> Admin
          </a>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            className="flex items-center gap-2 text-clay-text hover:text-clay-gold transition-colors p-1"
          >
            <Menu className="w-7 h-7" />
            <span className="hidden md:inline font-mono text-xs uppercase tracking-[0.25em]">Menu</span>
          </button>
          <button
            data-testid="nav-join-button"
            onClick={onJoin}
            className="bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase tracking-wider text-sm px-5 py-2.5 transition-all duration-300 hover:-translate-y-0.5 clay-glow whitespace-nowrap"
          >
            Join — $35
          </button>
        </div>
      </div>
    </motion.header>

    <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] bg-clay-bg/95 backdrop-blur-xl flex flex-col"
          >
            <div className="flex items-center justify-between px-4 sm:px-8 py-4 border-b border-clay-border">
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-clay-muted">Menu</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                className="text-clay-muted hover:text-clay-text transition-colors p-1"
              >
                <X className="w-7 h-7" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 overflow-y-auto">
              {MENU_LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={l.testid}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                  onClick={() => go(l.href)}
                  className={`text-left font-display font-black uppercase tracking-tight text-4xl py-3 transition-colors duration-300 border-b border-clay-border/40 ${
                    l.accent ? "text-clay-orange hover:text-clay-gold" : "text-clay-text hover:text-clay-orange"
                  }`}
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <div className="p-8">
              <button
                data-testid="menu-join-button"
                onClick={() => { setMenuOpen(false); onJoin(); }}
                className="w-full bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-8 py-4 transition-colors duration-300 clay-glow"
              >
                Become a Member — from $15
              </button>
            </div>
          </motion.div>
        )}
    </AnimatePresence>
    </>
  );
}
